import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { ColorPrimary } from "@intelligems/ig-theme";
import { ExpandLess } from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import {
  Badge,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../api/configurations/use-configuration";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { BaseRoute, MainRoute } from "../../types/navigation";
import { checkAnyScope } from "../ScopeWrappers/helpers";
import { activeRoute } from "../Utils/helpers";

const PREFIX = "ListItemLink";

const classes = {
  selected: `${PREFIX}-selected`,
  root: `${PREFIX}-root`,
};

const StyledList = styled(List)(({ theme }) => ({
  [`& .${classes.selected}`]: {
    "&.MuiListItemButton-root": {
      paddingBottom: 4,
      paddingTop: 4,
      "&.Mui-selected": {
        color: ColorPrimary.light3,
        background: "transparent",
        "&:hover, &:focus": {
          background: "transparent",
          color: ColorPrimary.light3,
        },
        "&:after": {
          width: 5,
          height: 48,
          background: "rgb(25, 65, 225)",
          transform: "translateX(0)",
        },
      },
    },
  },
  ["& .MuiBadge-badge"]: {
    // transform: "rotate(22deg)",
    fontSize: "10px",
    marginRight: "-25px",
    marginTop: "13px",
  },

  [`& .${classes.root}`]: {
    // icons default
    "&.MuiListItemButton-root": {
      paddingBottom: 4,
      paddingTop: 4,
      color: "white",
      fontSize: "16px",
      "&:hover, &:focus": {
        background: "transparent",
        color: ColorPrimary.light3,
      },
    },
  },
  "& .MuiListItemText-root": {
    fontSize: "14px",
  },
}));

interface ListItemLinkProps {
  open?: boolean;
  handleClick?: () => void;
  drawerOpen: boolean;
  Route: MainRoute;
  exact?: boolean;
  hasAccess: boolean;
  isNew?: boolean;
}

const ListItemLink = ({
  open,
  handleClick,
  Route,
  exact,
  drawerOpen,
  hasAccess,
  isNew,
}: ListItemLinkProps) => {
  const { pathname } = useLocation();
  const isAdmin = useIsAdmin();

  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );
  const shouldShowDataReconciliation = useConfiguration<boolean>(
    "view_data_reconciliation_analytics"
  );

  const hasDataReconciliationAccess = checkAnyScope([
    isAdmin,
    shouldShowDataReconciliation?.value,
  ]);

  const filteredAccessRoutes = useMemo(() => {
    let availableRoutes = Route.routes;
    if (!shouldShowProfit?.value) {
      availableRoutes = availableRoutes.filter(
        (route) => route.sidebarName !== "Profit"
      );
    }

    if (!hasDataReconciliationAccess) {
      availableRoutes = availableRoutes.filter(
        (route) => route.sidebarName !== "Data Reconciliation"
      );
    }
    return availableRoutes;
  }, [Route.routes, shouldShowProfit?.value, hasDataReconciliationAccess]);

  if (shouldShowProfit?.value !== undefined) {
    return (
      <StyledList aria-labelledby="nested-list-subheader">
        <Link
          key={Route.path}
          style={{ textDecoration: "none" }}
          to={hasAccess ? Route.path : window.location.pathname}
          state={
            hasAccess
              ? { name: Route.routes[0]?.sidebarName.toLowerCase() }
              : {}
          }
        >
          <ListItemButton
            disabled={!hasAccess}
            disableTouchRipple
            dense={true}
            classes={{
              selected: Route.routes[0] ? undefined : classes.selected,
              root: classes.root,
            }}
            sx={{ pt: 1 }}
            selected={
              Route.routes[0] ? false : activeRoute(Route.path, pathname, exact)
            }
            onClick={handleClick}
          >
            <>
              {hasAccess ? (
                Route.routes[0] ? (
                  open ? (
                    <ExpandLess />
                  ) : (
                    Route.icon
                  )
                ) : (
                  Route.icon
                )
              ) : (
                <LockIcon />
              )}
              {isNew ? (
                <Badge badgeContent={"BETA"} color={"primary"}>
                  <ListItemText
                    disableTypography={true}
                    sx={{
                      marginLeft: "15px",
                      fontSize: "14px",
                    }}
                    primary={Route.sidebarName}
                  />
                </Badge>
              ) : (
                <ListItemText
                  disableTypography={true}
                  sx={{
                    marginLeft: "15px",
                    fontSize: "14px",
                  }}
                  primary={Route.sidebarName}
                />
              )}
            </>
          </ListItemButton>
        </Link>
        {hasAccess && open && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {filteredAccessRoutes.map((route: BaseRoute) => (
                <Link
                  key={route.path}
                  style={{ textDecoration: "none" }}
                  to={{
                    pathname: route.path,
                  }}
                  state={{ name: route?.sidebarName?.toLowerCase() }}
                >
                  <ListItemButton
                    disableTouchRipple
                    dense
                    key={route.path}
                    classes={{
                      selected: classes.selected,
                      root: classes.root,
                    }}
                    selected={activeRoute(route.path, pathname, exact)}
                    sx={drawerOpen ? { pl: 4 } : undefined}
                  >
                    {route.icon}
                    <ListItemText
                      key={route.path}
                      disableTypography={true}
                      sx={{
                        marginLeft: route.icon === undefined ? "22px" : "15px",
                      }}
                      primary={route.sidebarName}
                    />
                  </ListItemButton>
                </Link>
              ))}
            </List>
          </Collapse>
        )}
      </StyledList>
    );
  }
  return (
    <Box margin={"10px"}>
      <Skeleton />
    </Box>
  );
};

export default ListItemLink;
